import { useMemo } from "react";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography,
} from "@mui/material";
import { AccountBalanceWallet, Close } from "@mui/icons-material";
import { useFacilityDetails, useFactoryWallets } from "../api/walletQueries";
import type { VendorWalletBalance, FacilityDetail } from "../api/walletQueries";
import { formatAmount } from "../utils/formatters";

interface VendorWalletModalProps {
  open: boolean;
  onClose: () => void;
  factoryCode: string;
  factoryName?: string;
}

function WalletRow({ wallet, facilityName }: { wallet: VendorWalletBalance; facilityName: string }) {
  return (
    <Box sx={{ px: 3, py: 2, borderBottom: "1px solid #E6E6E6" }}>
      <Box display="flex" justifyContent="space-between" alignItems="baseline" mb={0.5}>
        <Typography fontSize="13px" color="text.secondary">
          {facilityName}
        </Typography>
        <Typography fontSize="18px" fontWeight={700} color="#3655F2">
          {formatAmount(wallet.balance)}
        </Typography>
      </Box>
      <Box display="flex" gap={3}>
        <Typography fontSize="12px" color="text.secondary">
          Credit:{" "}
          <span style={{ color: "#25704C", fontWeight: 600 }}>
            {formatAmount(wallet.total_credit)}
          </span>
        </Typography>
        <Typography fontSize="12px" color="text.secondary">
          Debit:{" "}
          <span style={{ color: "#c0392b", fontWeight: 600 }}>
            {formatAmount(wallet.total_debit)}
          </span>
        </Typography>
      </Box>
    </Box>
  );
}

export default function VendorWalletModal({
  open,
  onClose,
  factoryCode,
  factoryName,
}: VendorWalletModalProps) {
  const { data: facilities, isInitialLoading: idsLoading, isError: idsError } = useFacilityDetails(factoryCode);
  // Only facilities with a provisioned wallet — the popup lists balances, so a
  // wallet-less facility would just be a guaranteed failed fetch in failedCount.
  // (The Wallet tab shows them instead, as an explicit "no wallet yet" row.)
  const facilityIds = useMemo(
    () => (facilities ?? []).filter((f) => f.walletId != null).map((f) => f.id),
    [facilities],
  );
  const facilityNameMap = useMemo(
    () => Object.fromEntries((facilities ?? []).map((f: FacilityDetail) => [f.id, f.name])),
    [facilities],
  );
  const walletResults = useFactoryWallets(facilityIds);

  // Zip with facilityIds[i] as an index-based fallback in case vendor_facility_id is
  // absent from the response (avoids key={undefined} + "Facility #undefined" label).
  const wallets = walletResults
    .map((r, i) =>
      r.data ? { wallet: r.data, facilityId: r.data.vendor_facility_id ?? facilityIds[i] } : null,
    )
    .filter((entry): entry is { wallet: VendorWalletBalance; facilityId: number } => entry !== null);

  // isInitialLoading (not isLoading) — disabled queries in TanStack Query v4 report
  // isLoading:true / fetchStatus:idle, which would pin the spinner indefinitely.
  const isLoading = idsLoading || walletResults.some((r) => r.isInitialLoading);
  // Count failed wallet fetches (getWalletBalance swallows errors to null).
  const failedCount = !isLoading ? facilityIds.length - wallets.length : 0;
  // If we expected wallets (facilityIds.length > 0) but none loaded, surface an error
  // rather than the misleading "No wallet data found" empty state.
  const isError = idsError || (!isLoading && facilityIds.length > 0 && wallets.length === 0);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 1,
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <AccountBalanceWallet sx={{ fontSize: 20, color: "#3655F2" }} />
          <Typography fontSize="16px" fontWeight={600}>
            Vendor Wallet
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} aria-label="Close">
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>

      {factoryName && (
        <Box px={3} pb={1}>
          <Typography fontSize="12px" color="text.secondary">
            {factoryName}
          </Typography>
        </Box>
      )}

      <Divider />

      <DialogContent sx={{ p: 0, minHeight: 120 }}>
        {isLoading && (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress size={32} />
          </Box>
        )}
        {!isLoading && isError && (
          <Box py={5} textAlign="center">
            <Typography color="error" fontSize="14px">
              Couldn't load wallet balances. Please try again.
            </Typography>
          </Box>
        )}
        {!isLoading && !isError && wallets.length === 0 && (
          <Box py={5} textAlign="center">
            <Typography color="text.secondary" fontSize="14px">
              No wallet data found for this factory.
            </Typography>
          </Box>
        )}
        {!isLoading && !isError &&
          wallets.map(({ wallet, facilityId }) => (
            <WalletRow
              key={facilityId}
              wallet={wallet}
              facilityName={facilityNameMap[facilityId] ?? `Facility #${facilityId}`}
            />
          ))}
        {!isLoading && !isError && failedCount > 0 && (
          <Box px={3} py={1}>
            <Typography fontSize="12px" color="text.secondary">
              {failedCount} facility wallet{failedCount > 1 ? "s" : ""} could not be loaded.
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
